import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Text,
  Flex,
  Image,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { color } from './constants/color';
import { softwareLanguages } from './constants/data';
import useLogin from './useLogin';
import useRefreshJWT from './useRefreshJWT';

import BarStrip from './BarStrip';
import LanguageSelector from './LanguageSelector';
import calibriteLogo from './assets/Calibrite-colour-circle_greybg.svg';
import { EmailInputStage } from './EmailInputStage';
import { LoginStage } from './LoginStage';
import { ForgotPasswordStage } from './ForgotPasswordStage';
import { ResetPasswordStage } from './ResetPasswordStage';

const AccountLogin = ({ isOpen, onClose, apiUrl }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState('email-input');
  const [authToken, setAuthToken] = useState(null);
  const { refreshToken } = useRefreshJWT(apiUrl, 'users');
  const { login } = useLogin(apiUrl);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('payloadToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          // Token is still valid, show dashboard
          window.location.href = window.location.pathname;
          return;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
      // Token is invalid, remove it
      localStorage.removeItem('payloadToken');
    }
  }, []);

  const handleEmailSubmit = async (submittedEmail) => {
    setEmail(submittedEmail);
    setStage('login');
  };

  const handleLoginSubmit = async (user, token) => {
    setAuthToken(token); // Set the auth token before storing in localStorage
    localStorage.setItem('payloadToken', token);
    // Attempt to refresh the token to ensure it's valid
    await refreshToken(token);
    // Use the login function to complete the authentication process
    await login(user);
    // Redirect to trigger dashboard display
    window.location.href = window.location.pathname;
  };

  const handleBack = (to) => {
    setStage(typeof to === 'string' ? to : 'email-input');
  };

  const handleForgotPassword = () => {
    setStage('forgot-password');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} isCentered size="lg">
      <ModalOverlay />
      <ModalContent backgroundColor={color.grey} color={color.lightWhite} outline="0.063rem solid white">
        <ModalHeader>
          <Flex justifyContent="space-between">
            <Image width="40px" mb={2} src={calibriteLogo} />
            <LanguageSelector languages={softwareLanguages} />
          </Flex>
          <BarStrip />
          <Text mt={4} fontSize="2xl" fontWeight="bold">
            {t('heading.Login to your account')}
          </Text>
        </ModalHeader>

        <ModalBody>
          {stage === 'email-input' && (
            <EmailInputStage onNext={handleEmailSubmit} apiUrl={apiUrl} />
          )}
          {stage === 'login' && (
            <LoginStage
              onSubmit={handleLoginSubmit}
              email={email}
              onBack={handleBack}
              handleForgotPassword={handleForgotPassword}
              apiUrl={apiUrl}
            />
          )}
          {stage === 'forgot-password' && (
            <ForgotPasswordStage email={email} onBack={handleBack} apiUrl={apiUrl} />
          )}
          {stage === 'reset-password' && (
            <ResetPasswordStage token={authToken} apiUrl={apiUrl} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccountLogin; 